import { Base, nextFrame } from "@studiometa/js-toolkit";
import { wait } from "@studiometa/js-toolkit/utils";
import {
  themeIsDark,
  watchTheme,
  getTransformedHtml as getHtml,
  getTransformedStyle as getStyle,
  getTransformedScript as getScript
} from "../store/index.js";
import { resolveImportMapUrls } from "../utils/resolve-import-map-urls.js";
import { createPatchedUrl } from "../utils/patch-iframe-url.js";
class Iframe extends Base {
  /**
   * Config.
   */
  static config = {
    name: "Iframe",
    refs: ["iframe"],
    options: {
      tailwindcss: Boolean,
      syncColorScheme: Boolean,
      importMap: { type: Object, default: () => ({}) }
    }
  };
  /**
   * The script element inside the iframe used to inject the script editor's content.
   */
  script;
  /**
   * Is the esbuild worker ready?
   */
  static esbuild;
  /**
   * Esbuild initializer promise.
   */
  static esbuildPromise;
  /**
   * The style element inside the iframe used to inject the script editor's content.
   */
  style;
  /**
   * Unsubscribe the theme watcher installed by the current iframe document.
   *
   * `initIframe()` runs again on every reload, so the previous subscription
   * has to go before a new one replaces it.
   */
  #unwatchTheme;
  /**
   * The document the import map was registered into, if any.
   */
  #importMapDocument;
  get window() {
    return this.$refs.iframe.contentWindow;
  }
  get doc() {
    return this.window?.document;
  }
  async mounted() {
    await nextFrame();
    await this.initIframe();
    return () => {
      this.#unwatchTheme?.();
      this.#unwatchTheme = void 0;
    };
  }
  /**
   * Replace the preview's JavaScript realm, then build the document again.
   *
   * js-toolkit v4 registers a component class under its name in a
   * module-scoped registry, and a second registration of the same name is
   * ignored. Re-running an edited script inside the same realm therefore kept
   * the first version of every class the author wrote — a playground where
   * editing the script changes nothing. v3 had no registry, so re-running
   * `createApp()` was enough and this method did not have to exist.
   *
   * Navigating the frame is what drops the registry, because it drops the
   * realm and its module map with it.
   */
  async resetFrame() {
    const { iframe } = this.$refs;
    await new Promise((resolve) => {
      iframe.addEventListener("load", () => resolve(), { once: true });
      iframe.srcdoc = `<!doctype html><!--${Date.now()}-->`;
    });
    await this.initIframe();
  }
  async initIframe() {
    this.$refs.iframe.classList.add("opacity-0");
    this.window.__DEV__ = true;
    const iframeWindow = this.window;
    iframeWindow.URL = createPatchedUrl(iframeWindow.URL);
    const html = await getHtml();
    this.doc.documentElement.innerHTML = `
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
</head>
<body>
${html}
</body>`;
    if (this.$options.tailwindcss) {
      await this.initTailwind();
    }
    if (this.$options.syncColorScheme) {
      this.doc.documentElement.classList.toggle("dark", await themeIsDark());
      this.#unwatchTheme?.();
      this.#unwatchTheme = watchTheme((theme) => {
        this.doc.documentElement.classList.toggle("dark", theme === "dark");
      });
    }
    this.style = this.doc.createElement("style");
    this.style.id = "style";
    if (this.$options.tailwindcss) {
      this.style.type = "text/tailwindcss";
    }
    this.doc.head.append(this.style);
    this.$refs.iframe.classList.remove("opacity-0");
    await this.initImportMaps();
    this.script = this.doc.createElement("script");
    this.script.type = "module";
    this.script.id = "script";
    this.doc.head.append(this.script);
    await wait();
    await this.updateStyle();
    await this.updateScript(false);
  }
  async esbuild() {
    if (Iframe.esbuild) {
      return Iframe.esbuild;
    }
    if (Iframe.esbuildPromise) {
      return Iframe.esbuildPromise.promise;
    }
    Iframe.esbuildPromise = Promise.withResolvers();
    const esbuildUrl = `https://esm.sh/esbuild-wasm@${"0.28.2"}`;
    const mod = await import(
      /* webpackIgnore: true */
      esbuildUrl
    );
    const esbuild = mod.default ?? mod;
    await esbuild.initialize({
      wasmURL: `https://esm.sh/esbuild-wasm@${"0.28.2"}/esbuild.wasm`
    });
    Iframe.esbuild = esbuild;
    Iframe.esbuildPromise.resolve(esbuild);
    return esbuild;
  }
  async initImportMaps() {
    if (this.#importMapDocument === this.doc) {
      return;
    }
    this.#importMapDocument = this.doc;
    const importMap = this.doc.createElement("script");
    importMap.type = "importmap";
    importMap.textContent = JSON.stringify({
      imports: resolveImportMapUrls(this.$options.importMap)
    });
    this.doc.head.append(importMap);
  }
  async initTailwind() {
    return new Promise((resolve) => {
      const tailwindScript = this.doc.createElement("script");
      tailwindScript.src = "https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4";
      tailwindScript.id = "tw";
      tailwindScript.addEventListener("load", () => {
        const tailwindConfig = this.doc.createElement("style");
        tailwindConfig.type = "text/tailwindcss";
        tailwindConfig.textContent = "@custom-variant dark (&:where(.dark, .dark *));";
        this.doc.head.append(tailwindConfig);
        resolve();
      });
      this.doc.head.append(tailwindScript);
    });
  }
  async updateHtml() {
    console.log("updating html...");
    await wait();
    const html = await getHtml();
    if (html !== this.doc.body.innerHTML) {
      this.doc.body.innerHTML = html;
    }
    await wait();
    console.log("html updated!");
    await this.updateScript(false);
  }
  async updateStyle() {
    console.log("updating style...");
    await wait();
    const style = await getStyle();
    this.style.textContent = style;
    await wait();
    console.log("style updated!");
  }
  async updateScript(resetHtml = true) {
    console.log("updating script...");
    if (resetHtml) {
      await this.resetFrame();
      return;
    }
    await wait();
    const newScriptContent = await getScript();
    const newScript = `${newScriptContent}
document.dispatchEvent(new Event("readystatechange"))`;
    try {
      const esbuild = await this.esbuild();
      const results = await esbuild.transform(newScript, {
        target: "es2020",
        loader: "ts"
      });
      this.script.remove();
      this.script = this.doc.createElement("script");
      this.script.type = "module";
      this.script.id = "script";
      this.script.textContent = results.code;
      this.doc.head.append(this.script);
      console.log("script updated!");
    } catch (err) {
      console.log("script not updated due to some errors:");
      if (Array.isArray(err.errors)) {
        for (const error of err.errors) {
          console.error(`${error.text} (${error.location.line}:${error.location.column})`);
        }
      } else {
        console.error(err);
      }
    }
  }
}
export {
  Iframe as default
};
//# sourceMappingURL=Iframe.js.map
